import React, { useRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Form } from 'antd';
import { getSummariesEnquiry, sendMessage } from 'redux/enquiries/actions';
import { getAllDocuments } from 'redux/applications/actions';
import i18next from 'i18next';
import { useParams } from 'react-router';
import { isEmpty, omit } from 'lodash-es';
import { SendIcon } from 'components/common/SVGIcon';
import { ENQUIRIES_STATUS_CONST } from 'configs/localData';
import { enquiriesSelectors } from 'redux/enquiries/selectors';
import CustomButton from 'components/RestActions/CustomButton';
import { useAppDispatch } from 'redux/hooks';
import { ListMessageItem, EnquiriesResourceFilter } from 'types/enquiries';
import PreviewFiles from '../UploadFileMessage/PreviewFiles';
import UploadFileMessage from '../UploadFileMessage';
import { useFilesMessage } from '../UploadFileMessage/context';
import { FooterFormStyles } from './styles';

const sourceFiles = 'files';

export interface FooterProps {
  id: string;
  isUploadFile: boolean;
  isRefreshDocument?: boolean;
  applicationId?: string;
  centreResponded?: boolean;
}

interface ResponseItem {
  data?: ListMessageItem;
  inboxId?: string;
}

const MessageFooter = ({
  id,
  isUploadFile,
  isRefreshDocument,
  applicationId,
  centreResponded,
}: FooterProps) => {
  const dispatch = useAppDispatch();

  const { tab } = useParams();

  const ref = useRef(null);

  const { clearFileList } = useFilesMessage();

  const [form] = Form.useForm();

  const { validateFields, resetFields, setFieldsValue } = form;

  const [isDisabledSubmit, setIsDisabledSubmit] = useState(false);

  const [loading, setLoading] = useState(false);

  const resourceFilter: EnquiriesResourceFilter =
    useSelector(enquiriesSelectors.getFilters) || {};

  const currentInbox = useSelector(enquiriesSelectors.getCurrentData) || {};

  const formatListFile = useCallback(
    (files) =>
      files
        ?.filter((item) => item?.status !== 'error')
        ?.map((file) => omit(file, 'status')),
    [],
  );

  const onSubmit = () => {
    if (!id) return;
    validateFields().then(({ files, message }) => {
      if (isEmpty(message?.trim()) && isEmpty(files)) return;
      const filesFormat = formatListFile(files);
      setLoading(true);
      dispatch(
        sendMessage({
          id,
          data: {
            ...(message?.trim() !== '' && { message }),
            ...(filesFormat?.length > 0 && {
              files: filesFormat,
            }),
          },
          centreResponded,
          isRemoveItem:
            (!centreResponded &&
              tab === ENQUIRIES_STATUS_CONST.notResponded.key) ||
            tab === ENQUIRIES_STATUS_CONST.archived.key,
        }),
      )
        .then(({ payload }: { payload: ResponseItem }) => {
          if (payload?.data?.id && currentInbox?.archivedAt) {
            dispatch(
              getSummariesEnquiry({
                data: { q: resourceFilter?.q, filter: resourceFilter?.filter },
              }),
            );
          }

          if (applicationId && isRefreshDocument && filesFormat?.length > 0) {
            dispatch(
              getAllDocuments({
                data: { id: applicationId, orderBy: '-createdAt' },
                options: { isRefresh: true },
              }),
            );
          }

          clearFileList();

          setLoading(false);

          const messageBody = document.getElementById('message-body');

          if (messageBody) {
            messageBody.scrollTop = messageBody.scrollHeight;
          }

          resetFields();

          const timeout = setTimeout(() => {
            ref.current.focus();
            clearTimeout(timeout);
          }, 50);
        })
        .catch(() => setLoading(false));
    });
  };

  const handlePressEnter = (evt) => {
    if (
      evt.keyCode === 13 &&
      (evt.ctrlKey || evt.metaKey) &&
      !isDisabledSubmit
    ) {
      evt.preventDefault();
      onSubmit();
    }
  };

  return (
    <FooterFormStyles>
      <PreviewFiles source={sourceFiles} setFieldsValue={setFieldsValue} />
      <Form onFinish={onSubmit} form={form}>
        <div className="content-type-text">
          {isUploadFile && (
            <UploadFileMessage
              form={form}
              source={sourceFiles}
              setIsDisabledSubmit={setIsDisabledSubmit}
            />
          )}
          <Form.Item noStyle name="message">
            <Input.TextArea
              ref={ref}
              placeholder={i18next.t('enquiries.typeMessage')}
              onPressEnter={handlePressEnter}
              autoSize={{ minRows: 1, maxRows: 3 }}
              bordered={false}
            />
          </Form.Item>
        </div>
        <Form.Item>
          <div className="ml-16">
            <CustomButton
              isBlur={currentInbox?.isBlur}
              loading={loading}
              buttonProps={{
                htmlType: 'submit',
                type: 'primary',
                icon: <SendIcon />,
                className: 'btn-second-primary w-80 h-50 text-24',
              }}
              title={null}
            />
          </div>
        </Form.Item>
      </Form>
    </FooterFormStyles>
  );
};

export default MessageFooter;
